import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { sendRfq, updateQuote, awardVendor } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Send, Award, Mail, AlertCircle, CheckCircle2, Save, MapPin } from "lucide-react";
import { toast } from "sonner";

const RFQ_STATUS_STYLES = {
  Sent: "bg-blue-100 text-blue-700",
  Quoted: "bg-violet-100 text-violet-700",
  Awarded: "bg-emerald-100 text-emerald-700",
  "Not Selected": "bg-neutral-200 text-neutral-600",
  Failed: "bg-red-100 text-red-700",
  "No Email": "bg-amber-100 text-amber-800",
};

const money = (v) => (v === null || v === undefined || v === "" ? "—" : `$${Number(v).toLocaleString()}`);

// best-effort match of a vendor region against the job's pickup/delivery text
const regionMatchesRoute = (region, routeText) => {
  if (!region || region === "Unassigned" || !routeText || !routeText.trim()) return false;
  const rt = routeText.toLowerCase();
  const rtoks = region.toLowerCase().split(/\s+/).filter((t) => t.length > 3);
  const ltoks = rt.split(/\s+/).filter((t) => t.length > 3);
  return rtoks.some((t) => rt.includes(t)) || ltoks.some((t) => region.toLowerCase().includes(t));
};

export const RfqPanel = ({ reqId, rfqs = [], vendors, loadInfo, onUpdated }) => {
  const [selected, setSelected] = useState([]);
  const [quoteInputs, setQuoteInputs] = useState({});

  const sendMut = useMutation({
    mutationFn: () => sendRfq(reqId, selected),
    onSuccess: (data) => {
      const okCount = data.results.filter((r) => r.ok).length;
      const failed = data.results.filter((r) => !r.ok);
      if (okCount) toast.success(`RFQ sent to ${okCount} vendor${okCount > 1 ? "s" : ""}`);
      failed.forEach((f) => toast.error(`${f.vendor_name}: ${f.error}`));
      setSelected([]);
      onUpdated(data.requisition);
    },
    onError: () => toast.error("Failed to send RFQ"),
  });

  const quoteMut = useMutation({
    mutationFn: ({ vendorId, amount }) => updateQuote(reqId, vendorId, { quote_amount: amount }),
    onSuccess: (req) => { toast.success("Quote recorded"); onUpdated(req); },
    onError: () => toast.error("Failed to record quote"),
  });

  const awardMut = useMutation({
    mutationFn: (vendorId) => awardVendor(reqId, vendorId),
    onSuccess: (req) => { toast.success("Vendor awarded — carrier & cost updated"); onUpdated(req); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to award vendor"),
  });

  // Auto-preselect vendors whose region matches this job's route (once per requisition)
  useEffect(() => {
    if (!reqId) return;
    const rt = `${loadInfo?.pickup_location || ""} ${loadInfo?.delivery_location || ""}`;
    const preset = vendors.filter((v) => regionMatchesRoute(v.region?.trim(), rt) && v.email).map((v) => v.id);
    if (preset.length) setSelected(preset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reqId]);

  if (!reqId) {
    return (
      <div data-testid="rfq-panel-locked" className="rounded-md border border-dashed border-border/60 bg-neutral-50 px-4 py-6 text-center text-sm text-muted-foreground">
        <Mail className="h-5 w-5 mx-auto mb-2 text-neutral-400" />
        Save this requisition first, then send RFQs to vendors from here.
      </div>
    );
  }

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  // group vendors by region for the RFQ selector
  const groups = {};
  vendors.forEach((v) => {
    const key = v.region?.trim() || "Unassigned";
    (groups[key] = groups[key] || []).push(v);
  });
  const routeText = `${loadInfo.pickup_location || ""} ${loadInfo.delivery_location || ""}`;
  const isSuggested = (r) => regionMatchesRoute(r, routeText);
  const groupNames = Object.keys(groups).sort((a, b) => {
    const sa = isSuggested(a), sb = isSuggested(b);
    if (sa !== sb) return sa ? -1 : 1;
    if (a === "Unassigned") return 1;
    if (b === "Unassigned") return -1;
    return a.localeCompare(b);
  });
  const toggleGroup = (names) => {
    const ids = groups[names].map((v) => v.id);
    const allOn = ids.every((id) => selected.includes(id));
    setSelected((p) => (allOn ? p.filter((x) => !ids.includes(x)) : [...new Set([...p, ...ids])]));
  };

  return (
    <div className="space-y-4" data-testid="rfq-panel">
      <div className="rounded-md border border-border/60 bg-neutral-50/60 px-3 py-2.5 text-xs text-neutral-600">
        <span className="font-semibold text-neutral-800">RFQ includes:</span>{" "}
        {[
          loadInfo.load_dimensions && `Dims ${loadInfo.load_dimensions}`,
          loadInfo.estimated_weight && `Weight ${loadInfo.estimated_weight}`,
          loadInfo.pickup_location && `From ${loadInfo.pickup_location}`,
          loadInfo.delivery_location && `To ${loadInfo.delivery_location}`,
        ].filter(Boolean).join(" · ") || "load details from this requisition"}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-[#0055FF]">Select Vendors to Quote — by Region</h4>
          <Button size="sm" data-testid="send-rfq-btn" disabled={!selected.length || sendMut.isPending}
            onClick={() => sendMut.mutate()} className="gap-2 bg-[#0055FF] hover:bg-[#0044CC] h-8">
            <Send className="h-3.5 w-3.5" />
            {sendMut.isPending ? "Sending…" : `Send RFQ${selected.length ? ` (${selected.length})` : ""}`}
          </Button>
        </div>
        <div className="max-h-56 overflow-auto rounded-md border border-border/60 divide-y divide-border/50">
          {vendors.length === 0 && <div className="px-3 py-4 text-sm text-muted-foreground text-center">No vendors yet.</div>}
          {groupNames.map((gname) => {
            const ids = groups[gname].map((v) => v.id);
            const allOn = ids.every((id) => selected.includes(id));
            return (
              <div key={gname} data-testid={`rfq-region-${gname}`}>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-neutral-50 sticky top-0">
                  <MapPin className="h-3.5 w-3.5 text-[#0055FF]" />
                  <span className="text-[11px] font-bold uppercase tracking-wide text-neutral-700">{gname} <span className="text-neutral-400">({groups[gname].length})</span></span>
                  {isSuggested(gname) && <span data-testid={`rfq-region-suggested-${gname}`} className="text-[9px] font-bold uppercase bg-amber-100 text-amber-700 rounded px-1.5 py-0.5">Suggested</span>}
                  <span className="flex-1" />
                  <button type="button" data-testid={`rfq-region-all-${gname}`} onClick={() => toggleGroup(gname)}
                    className="text-[11px] font-semibold text-[#0055FF] hover:underline">{allOn ? "Clear" : "Select all"}</button>
                </div>
                {groups[gname].map((v) => (
                  <label key={v.id} data-testid={`rfq-vendor-${v.id}`}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50 cursor-pointer text-sm">
                    <Checkbox checked={selected.includes(v.id)} onCheckedChange={() => toggle(v.id)} data-testid={`rfq-check-${v.id}`} />
                    <span className="flex-1 truncate">{v.carrier_name}</span>
                    {v.email ? (
                      <span className="text-xs text-muted-foreground font-mono-data truncate max-w-[180px]">{v.email}</span>
                    ) : (
                      <span className="text-xs text-amber-600 inline-flex items-center gap-1"><AlertCircle className="h-3 w-3" />no email</span>
                    )}
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {rfqs.length > 0 && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-700 mb-2">Sent RFQs & Quotes</h4>
          <div className="rounded-md border border-border/60 overflow-hidden">
            {rfqs.map((r) => (
              <div key={r.vendor_id} data-testid={`rfq-row-${r.vendor_id}`}
                className="flex flex-wrap items-center gap-2 px-3 py-2.5 border-b border-border/50 last:border-0 text-sm">
                <div className="flex-1 min-w-[140px]">
                  <div className="font-medium truncate">{r.vendor_name}</div>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold mt-0.5 ${RFQ_STATUS_STYLES[r.status] || "bg-neutral-200 text-neutral-600"}`}>
                    {r.status === "Awarded" && <CheckCircle2 className="h-3 w-3 mr-1" />}
                    {r.status}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">Quote</span>
                  <Input type="number" data-testid={`rfq-quote-input-${r.vendor_id}`}
                    className="h-8 w-24 font-mono-data"
                    placeholder={r.quote_amount != null ? String(r.quote_amount) : "0"}
                    value={quoteInputs[r.vendor_id] ?? ""}
                    onChange={(e) => setQuoteInputs((p) => ({ ...p, [r.vendor_id]: e.target.value }))} />
                  <Button size="icon" variant="outline" className="h-8 w-8" data-testid={`rfq-save-quote-${r.vendor_id}`}
                    disabled={quoteInputs[r.vendor_id] === undefined || quoteInputs[r.vendor_id] === "" || ["Failed", "No Email"].includes(r.status)}
                    onClick={() => quoteMut.mutate({ vendorId: r.vendor_id, amount: Number(quoteInputs[r.vendor_id]) })}>
                    <Save className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="w-16 text-right font-mono-data text-sm font-semibold">{money(r.quote_amount)}</div>
                <Button size="sm" variant={r.status === "Awarded" ? "secondary" : "outline"}
                  data-testid={`rfq-award-${r.vendor_id}`} className="h-8 gap-1.5"
                  disabled={r.status === "Awarded" || ["Failed", "No Email"].includes(r.status)} onClick={() => awardMut.mutate(r.vendor_id)}>
                  <Award className="h-3.5 w-3.5" />
                  {r.status === "Awarded" ? "Awarded" : "Award"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
